import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { get, put } from '@/lib/api'

// ==================== Types ====================

export type ConfigCategory = 'general' | 'currency' | 'notification' | 'security'
export type ConfigType = 'string' | 'number' | 'boolean' | 'enum'

export interface ConfigDefinition {
  key: string
  name: string
  description: string
  category: ConfigCategory
  type: ConfigType
  defaultValue: unknown
  options?: string[]
  validation?: {
    min?: number
    max?: number
    pattern?: string
  }
}

export interface ConfigItem {
  key: string
  value: unknown
  definition: ConfigDefinition
  updatedAt?: string | null
  updatedBy?: string | null
}

export interface ConfigGroup {
  category: ConfigCategory
  name: string
  configs: ConfigItem[]
}

export interface UpdateConfigInput {
  value: unknown
}

// ==================== Query Keys ====================

export const systemConfigKeys = {
  all: ['system-config'] as const,
  lists: () => [...systemConfigKeys.all, 'list'] as const,
  list: () => [...systemConfigKeys.lists()] as const,
  detail: (key: string) => [...systemConfigKeys.all, 'detail', key] as const,
}

// ==================== Hooks ====================

/**
 * 获取所有系统配置（按分类分组）
 */
export function useSystemConfigs() {
  return useQuery({
    queryKey: systemConfigKeys.list(),
    queryFn: () => get<ConfigGroup[]>('/system/config'),
  })
}

/**
 * 获取单个配置项
 */
export function useSystemConfig(key: string) {
  return useQuery({
    queryKey: systemConfigKeys.detail(key),
    queryFn: () => get<ConfigItem>(`/system/config/${encodeURIComponent(key)}`),
    enabled: !!key,
  })
}

/**
 * 更新配置项
 */
export function useUpdateSystemConfig() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ key, data }: { key: string; data: UpdateConfigInput }) =>
      put<ConfigItem>(`/system/config/${encodeURIComponent(key)}`, data),
    onSuccess: (_, { key }) => {
      void queryClient.invalidateQueries({ queryKey: systemConfigKeys.detail(key) })
      void queryClient.invalidateQueries({ queryKey: systemConfigKeys.lists() })
    },
  })
}

/**
 * 按分类获取配置
 */
export function useSystemConfigsByCategory(category: ConfigCategory) {
  const { data, ...rest } = useSystemConfigs()
  
  const filteredData = data?.filter(group => group.category === category)
  
  return { data: filteredData, ...rest }
}
